import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CheckIcon, LocationIcon, StarIcon, DrumstickIcon } from '../components/Icons'
import './Franchise.css'

const benefits = [
  { icon: <CheckIcon size={35} />, title: 'Proven Success', description: 'Track record of profitability with 13+ years of experience' },
  { icon: <LocationIcon size={35} />, title: 'Scalable Model', description: 'Start with one location, expand to multiple stores' },
  { icon: <StarIcon size={35} color="#FFD700" />, title: 'Strong ROI', description: '36-40 months ROI period with proven profitability' },
  { icon: <DrumstickIcon size={35} />, title: 'Brand Loyalty', description: 'Join a brand loved by over 1 crore customers' },
  { icon: <CheckIcon size={35} />, title: 'Full Support', description: 'We handle operations, you own the business' },
  { icon: <LocationIcon size={35} />, title: 'Remote Operation', description: 'Operate from anywhere in the world' },
]

const faqs = [
  { 
    question: 'What is the FOCO model?',
    answer: 'FOCO stands for Franchise-Owned, Company-Operated. You own the location, and BIGGUYS handles all day-to-day operations including staff, inventory, and customer service.'
  },
  { 
    question: 'What is the total investment required?',
    answer: 'Investment options range from 30-40 Lacs, 40-50 Lacs, to 50+ Lacs depending on location and store size.'
  },
  { 
    question: 'Do I need prior experience?',
    answer: 'No prior food or business experience is required. Our comprehensive support system handles all operational aspects.'
  },
  { 
    question: 'What is the agreement term?',
    answer: 'The franchise agreement is for 6 years, renewable at 50% of the initial franchise fee.'
  },
  { 
    question: 'What are the monthly expenses?',
    answer: 'Your only recurring expense is store rent. All other operational costs are managed by the company.'
  },
]

const locations = [
  { city: 'Bangalore', status: 'Sold Out' },
  { city: 'Karnataka', status: 'Available' },
  { city: 'Hyderabad', status: 'Available' },
  { city: 'Telangana', status: 'Available' },
  { city: 'Andhra Pradesh', status: 'Available' },
  { city: 'Chennai', status: 'Available' },
  { city: 'Tamil Nadu', status: 'Available' },
  { city: 'Kerala', status: 'Available' },
]

export default function Franchise() {
  const [openFaq, setOpenFaq] = useState(null)
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', city: '', timeline: '', budget: '', experience: ''
  })
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.franchise-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Benefits animation
      gsap.fromTo('.benefit-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.benefits-section',
            start: 'top 70%',
          }
        }
      )

      // Stats animation
      gsap.fromTo('.franchise-stat',
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.franchise-stats',
            start: 'top 75%',
          }
        }
      )

      // Locations animation
      gsap.fromTo('.location-item',
        { opacity: 0, x: -20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.locations-grid',
            start: 'top 75%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    alert('Thank you for your interest! Our team will contact you soon.')
  }

  return (
    <div className="franchise-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="franchise-hero">
        <div className="container">
          <div className="franchise-hero-content">
            <span className="hero-label">Franchise Opportunity</span>
            <h1 className="hero-title">
              Own a Piece of
              <span className="highlight"> India's Growing QSR</span>
            </h1>
            <p className="hero-description">
              Join the BIGGUYS family with our FOCO (Franchise-Owned, Company-Operated) model. 
              You invest, we operate - it's that simple.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="franchise-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="franchise-stat">
              <span className="stat-number">36-40</span>
              <span className="stat-label">Months ROI</span>
            </div>
            <div className="franchise-stat">
              <span className="stat-number">45-60</span>
              <span className="stat-label">Days Setup</span>
            </div>
            <div className="franchise-stat">
              <span className="stat-number">6</span>
              <span className="stat-label">Year Agreement</span>
            </div>
            <div className="franchise-stat">
              <span className="stat-number">0</span>
              <span className="stat-label">Monthly Fees*</span>
            </div>
          </div>
          <p className="stats-note">*Only store rent is franchisee's responsibility</p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Why BIGGUYS?</span>
            <h2 className="section-title">Benefits of Partnering With Us</h2>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div className="benefit-card" key={index}>
                <span className="benefit-icon">{benefit.icon}</span>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="locations-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Expansion</span>
            <h2 className="section-title">Available Territories</h2>
          </div>

          <div className="locations-grid">
            {locations.map((location, index) => (
              <div className={`location-item ${location.status === 'Sold Out' ? 'sold-out' : ''}`} key={index}>
                <span className="location-city">{location.city}</span>
                <span className={`location-status ${location.status === 'Sold Out' ? 'unavailable' : 'available'}`}>
                  {location.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="franchise-form-section">
        <div className="container">
          <div className="form-grid">
            <div className="form-info">
              <span className="section-label">Get Started</span>
              <h2 className="section-title">Apply for a Franchise</h2>
              <p>
                Take the first step towards owning a BIGGUYS franchise. Fill out the form 
                and our team will get back to you within 48 hours.
              </p>
              
              {/* FAQ Accordion */}
              <div className="faq-section">
                <h3>Frequently Asked Questions</h3>
                {faqs.map((faq, index) => (
                  <div className="faq-item" key={index}>
                    <button 
                      className={`faq-question ${openFaq === index ? 'open' : ''}`}
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      {faq.question}
                      <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-container">
              <form onSubmit={handleSubmit} className="franchise-form">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input 
                      type="email" 
                      className="form-input" 
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input 
                      type="tel" 
                      className="form-input" 
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Preferred City *</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Investment Budget *</label>
                  <select 
                    className="form-input"
                    required
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  >
                    <option value="">Select budget range</option>
                    <option value="30-40">₹30-40 Lacs</option>
                    <option value="40-50">₹40-50 Lacs</option>
                    <option value="50+">₹50+ Lacs</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Timeline to Open</label>
                  <select 
                    className="form-input"
                    value={formData.timeline}
                    onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediately</option>
                    <option value="1-3">1-3 Months</option>
                    <option value="3-6">3-6 Months</option>
                    <option value="6+">6+ Months</option>
                  </select>
                </div>
                <button type="submit" className="btn btn-primary btn-full">
                  Submit Application
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
